import { useState } from 'react';

import { Spinner } from '../../components';
import { SubmittedMeter } from '../exams/ExamCharts';
import { RANK_BADGE_COLOR } from '../exams/examResults';
import { ClassListTools } from '../results/ClassListTools';
import { filterByName } from '../results/nameSearch';
import { ResultsCardFrame } from '../results/ResultsCardFrame';
import type { ResultSession } from '../results/sessions';
import { useResultSessionStore } from '../results/store';
import { formatAverage, taskGradingStats, topMarks, type GradingStats } from './gradingStats';
import { classEntries, statusLabel, vnDateTime, type RosterEntry, type StudentSubmission, type SubmissionTask } from './submissions';
import { useClassSubmissions } from './useClassSubmissions';

const TOP_MARKS = 3;

function statusClass(submission: StudentSubmission | null): string {
  if (!submission) return 'hw-status hw-status--missing';
  return submission.approved ? 'hw-status hw-status--marked' : 'hw-status hw-status--submitted';
}

function GradingSummary({ stats }: { stats: GradingStats }) {
  const bands = stats.bands;
  const max = bands ? Math.max.apply(null, bands.map((band) => band.count)) : 0;
  return (
    <section className="quiz-results__block">
      <h3 className="quiz-results__block-title">Phân bố điểm</h3>
      <div className="hw-stats">
        <p className="hw-stats__line">
          <strong>{stats.graded}/{stats.submitted}</strong> bài đã chấm
          {stats.average !== null ? <> • Điểm TB <strong>{formatAverage(stats.average)}</strong></> : null}
        </p>
        {bands ? (
          <div className="hw-stats__bars" role="img" aria-label={`Phân bố điểm: ${bands.map((band) => `${band.label}: ${band.count}`).join(', ')}`}>
            {bands.map((band) => (
              <div key={band.label} className="hw-stats__band">
                <span className="hw-stats__count">{band.count}</span>
                <span className="hw-stats__track"><span className="hw-stats__fill" style={{ height: `${max > 0 ? Math.round((band.count / max) * 100) : 0}%`, background: band.color }} /></span>
                <span className="hw-stats__label">{band.label}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

// Báo cáo: who handed in, the best marks, and the mark spread
function HomeworkReport({ task, total, activeCode, onOpen }: { task: SubmissionTask; total: number; activeCode: string | null; onOpen: (code: string) => void }) {
  const top = topMarks(Object.keys(task.byCode).map((code) => task.byCode[code]), TOP_MARKS);
  return (
    <>
      <SubmittedMeter submitted={task.count} total={total} />
      <section className="quiz-results__block">
        <h3 className="quiz-results__block-title">Học sinh điểm cao nhất</h3>
        {top.length === 0 ? <p className="quiz-results__empty">Chưa có bài nào được chấm điểm.</p> : (
          <ol className="quiz-results__ranks">
            {top.map((row) => (
              <li key={row.code} className={row.code === activeCode ? 'quiz-results__rank-row quiz-results__rank-row--active' : 'quiz-results__rank-row'}>
                <button type="button" className="quiz-results__rank-open" aria-label={`Mở bài nộp của ${row.name} lên bảng`} aria-current={row.code === activeCode ? 'true' : undefined} onClick={() => onOpen(row.code)}>
                  <span className="quiz-results__rank-badge" style={{ background: RANK_BADGE_COLOR }}>{row.rank}</span>
                  <span className="quiz-results__rank-name">{row.name}</span>
                  <span className="quiz-results__rank-meta"><span className="quiz-results__rank-score">{formatAverage(row.mark)} điểm</span></span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </section>
      <GradingSummary stats={taskGradingStats(task)} />
    </>
  );
}

function HomeworkRow({ entry, active, onOpen }: { entry: RosterEntry; active: boolean; onOpen: (code: string) => void }) {
  const submission = entry.submission;
  const body = (
    <span className="results-row__main">
      <span className="results-row__name">{entry.student.name}</span>
      <span className="results-row__sub">
        <span className={statusClass(submission)}>{statusLabel(submission)}</span>
        {submission && submission.submittedAt ? <span className="results-row__time">{vnDateTime(submission.submittedAt)}</span> : null}
      </span>
    </span>
  );
  return (
    <li className={active ? 'results-row is-active' : 'results-row'}>
      {submission ? (
        <button type="button" className="results-row__inner" aria-current={active ? 'true' : undefined} aria-label={`${entry.student.name}, ${statusLabel(submission)}, mở bài nộp lên bảng`} onClick={() => onOpen(entry.student.code)}>
          {body}
          <span className="results-row__end"><span className="results-row__hint">{active ? 'Đang xem' : 'Xem bài làm'}</span></span>
        </button>
      ) : <div className="results-row__inner">{body}</div>}
    </li>
  );
}

export interface HomeworkResultsCardProps {
  session: ResultSession;
  onClose: () => void;
  onBackToDe: () => void;
}

// Kết quả for the homework on this tab: Báo cáo and the whole class in Chi tiết
export function HomeworkResultsCard({ session, onClose, onBackToDe }: HomeworkResultsCardProps) {
  const { roster, loading, error, tasks, courseTitles, reload } = useClassSubmissions();
  const setPanelTab = useResultSessionStore((state) => state.setPanelTab);
  const openStudent = useResultSessionStore((state) => state.openStudent);
  const [query, setQuery] = useState('');
  const task = tasks.filter((item) => item.id === session.refId)[0] || null;
  const onOpen = (code: string) => openStudent(session.tabId, code);

  const subtitle = [task ? task.title : session.title, task ? courseTitles[task.courseId] || '' : ''].filter(Boolean).join(' • ');
  const actions = session.student ? <button type="button" className="hw-panel__refresh" onClick={onBackToDe}>Quay lại đề</button> : null;

  let body;
  if (!task) {
    body = loading || roster.length === 0 ? <Spinner label="Đang tải bài nộp của lớp…" />
      : error ? <p className="quiz-results__empty">Chưa tải được bài nộp: {error} <button type="button" className="hw-panel__refresh" onClick={reload}>Thử lại</button></p>
        : <p className="quiz-results__empty">Chưa có học sinh nào nộp bài này trên IZTeach.</p>;
  } else if (session.panelTab === 'report') {
    body = <HomeworkReport task={task} total={roster.length} activeCode={session.student} onOpen={onOpen} />;
  } else {
    const entries = filterByName(classEntries(task, roster), query, (entry) => `${entry.student.name} ${entry.student.code}`);
    body = (
      <section className="quiz-results__block">
        <ClassListTools query={query} onQuery={setQuery} onReload={reload} reloading={loading} />
        <div className="results-list__head"><span>{query.trim() ? `${entries.length} / ${roster.length} học sinh` : `${roster.length} học sinh`}</span><span>{task.count} đã nộp</span></div>
        {entries.length === 0 ? <p className="quiz-results__empty">Không có học sinh nào khớp tên này.</p> : (
          <ul className="results-list">
            {entries.map((entry) => <HomeworkRow key={entry.student.code} entry={entry} active={entry.student.code === session.student} onOpen={onOpen} />)}
          </ul>
        )}
      </section>
    );
  }

  return (
    <ResultsCardFrame label="Kết quả bài tập" subtitle={subtitle} actions={actions} tab={session.panelTab} onTab={(tab) => setPanelTab(session.tabId, tab)} onClose={onClose}>
      {body}
    </ResultsCardFrame>
  );
}
